import type { ChatEntry, Suggestion } from '@/contexts/ChatContext';
import type { ChatHistoryItem, ChatHistoryToolCall, ChatHistoryToolCallOutput } from '@/hooks/useWorkflowExecutions';
import type { RenderPayload, UIBlock, SelectBlock, RadioBlock, ContainerBlock } from '@/types/ui-blocks';
import type { CardBlock } from '@/hooks/useCards';
import { toRoutineDraftSnapshot, type RoutineDraftSnapshot } from '@/store/routineDraftStore';
import { normalizePayload } from '@/lib/block-normalizer';
import { getBlockTypeFromToolName, isExperienceType, assembleGroupedBlocks, buildExperienceBlock, extractGroupMeta } from '@/lib/tool-stream-utils';
import type { ToolStreamBlockMeta } from '@/lib/tool-stream-utils';

/**
 * Extracts plain display text from a raw user-message content string.
 * Handles three shapes that can arrive from the backend:
 *   - text block with behaviours  → returns block.content
 *   - ui_action event             → returns the selected label(s)
 *   - plain string                → returns as-is
 */
export function extractUserMessageText(content: string): string {
  try {
    const parsed = JSON.parse(content);
    // Text block (e.g. card-triggered report_preview)
    if (parsed?.type === 'text' && typeof parsed?.content === 'string') {
      return parsed.content;
    }
    // UI action event — pull out the human-readable label
    if (parsed?.type === 'ui_action' && parsed.state) {
      const state = parsed.state as Record<string, unknown>;
      const labels: string[] | null =
        Array.isArray(state.labels) ? state.labels
        : Array.isArray(state.values) ? state.values
        : typeof state.label === 'string' ? [state.label]
        : typeof state.value === 'string' ? [state.value]
        : typeof state.summary === 'string' ? [state.summary]
        : null;
      if (labels) {
        const joined = labels.filter(Boolean).join(', ');
        if (joined) return joined;
      }
    }
  } catch {
    // Not JSON — fall through
  }
  return content;
}

/**
 * Recovers the "next steps" suggestions from persisted history so a reloaded
 * chat can re-seed them. Only reads the LAST row and only when it is an
 * assistant turn — if the user has since replied, stale suggestions are
 * dropped. Mirrors the two shapes the live stream produces:
 *   - tool mode: a `ui_inline_suggestions` tool call carrying `{ items }`.
 *   - structured mode: a `stacked_suggestions` block in the content payload.
 */
export function extractLatestSuggestions(chatHistory: ChatHistoryItem[]): Suggestion[] {
  const last = chatHistory[chatHistory.length - 1];
  if (!last || last.role !== 'assistant') return [];

  // Tool mode — ui_inline_suggestions { items: [{ label, value }] }.
  const inlineTool = last.tool_calls?.find(
    (tc) => getBlockTypeFromToolName(tc.name) === 'inline_suggestions'
  );
  if (inlineTool) {
    try {
      const args = JSON.parse(inlineTool.arguments) as {
        items?: Array<{ label?: string; value?: string }>;
      };
      const suggestions = (args.items ?? []).flatMap((item, idx): Suggestion[] => {
        const text = item.label ?? item.value;
        return text ? [{ id: idx, text }] : [];
      });
      if (suggestions.length > 0) return suggestions;
    } catch {
      // Malformed args — fall through to the structured-mode check.
    }
  }

  // Structured mode — a stacked_suggestions block in the JSON content payload.
  if (last.content) {
    try {
      const parsed = JSON.parse(last.content) as {
        blocks?: Array<{
          type?: string;
          content?: { suggestions?: Array<{ id?: string; text?: string; prefillOnly?: boolean }> };
        }>;
      };
      const block = parsed.blocks?.find((b) => b.type === 'stacked_suggestions');
      const suggestions = (block?.content?.suggestions ?? []).flatMap((s): Suggestion[] =>
        s.text ? [{ id: s.id ?? s.text, text: s.text, prefillOnly: s.prefillOnly }] : []
      );
      if (suggestions.length > 0) return suggestions;
    } catch {
      // Not JSON / no blocks — no suggestions to recover.
    }
  }

  return [];
}

/**
 * Recovers the routine preview panel's state on reload. `ui_routine_draft`
 * leaves no message in the transcript, so the panel is rebuilt from the tool
 * call itself — the LAST one anywhere in history, since every call carries a
 * full snapshot and later ones supersede earlier ones outright.
 *
 * @param chatHistory - The thread's persisted history, oldest first.
 *
 * @returns The most recent snapshot, or null when the thread has none.
 */
export function extractLatestRoutineDraft(
  chatHistory: ChatHistoryItem[]
): RoutineDraftSnapshot | null {
  for (let i = chatHistory.length - 1; i >= 0; i--) {
    const toolCalls = chatHistory[i].tool_calls;
    if (!toolCalls?.length) continue;

    // Within a single turn the last draft call also wins.
    for (let j = toolCalls.length - 1; j >= 0; j--) {
      if (getBlockTypeFromToolName(toolCalls[j].name) !== 'routine_draft') continue;
      try {
        return toRoutineDraftSnapshot(JSON.parse(toolCalls[j].arguments));
      } catch {
        // Malformed args — keep scanning back for an earlier usable snapshot.
      }
    }
  }
  return null;
}

interface UIActionEvent {
  type: 'ui_action';
  component: 'select' | 'radio' | 'confirmation' | 'editable_list';
  label?: string;
  state: {
    values?: string[];
    value?: string;
    labels?: string[];
    label?: string;
    [key: string]: unknown;
  };
}

/** Runtime check that a deserialized value has the required CardBlock shape. */
function isCardBlock(value: unknown): value is CardBlock {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.type === 'string' &&
    typeof v.content === 'object' &&
    v.content !== null
  );
}

/**
 * Attempts to parse a user message as a UIBlock with behaviours.
 * These are produced when a card-triggered agent experience (e.g. report_preview)
 * opens a chat — the block carries both the display text and the behaviour metadata.
 * If a `cardBlock` field is present, it's extracted so the card visual can be restored.
 */
function parseUserBlock(content: string): { text: string; blocks: UIBlock[]; cardBlock?: CardBlock } | null {
  try {
    const parsed = JSON.parse(content);
    const hasBehaviours = Array.isArray(parsed?.behaviours) && parsed.behaviours.length > 0;
    const hasCardBlock = isCardBlock(parsed?.cardBlock);

    // Match text blocks that carry behaviours (e.g. card-triggered report_preview)
    // OR text blocks that carry a cardBlock (e.g. experiment suggestion posted to chat)
    if (parsed && parsed.type === 'text' && (hasBehaviours || hasCardBlock)) {
      const cardBlock = hasCardBlock ? parsed.cardBlock : undefined;
      // Spread parsed into a clean copy without cardBlock so it doesn't leak onto the UIBlock object
      const blockData = { ...parsed, content: '' };
      delete blockData.cardBlock;
      return {
        text: (parsed.content as string) || '',
        // Blank the block's own content — the text is rendered in the user bubble via ChatEntry.content.
        blocks: hasBehaviours ? [blockData as UIBlock] : [],
        cardBlock,
      };
    }
  } catch {
    // Not valid JSON
  }
  return null;
}

/**
 * Extracts a JSON blocks payload from content that may be wrapped in markdown code fences.
 *
 * Some LLMs (e.g. Claude) return structured JSON inside markdown fences:
 *   "Some preamble text\n\n```json\n{\"version\":\"v1\",\"blocks\":[...]}\n```"
 *
 * This function:
 *  1. Extracts the JSON from within the fences
 *  2. Prepends any preamble text as a leading text block so it's not lost
 *
 * Returns null if no code-fenced JSON with a valid blocks payload is found.
 */
function extractBlocksFromCodeFence(content: string | null): RenderPayload | null {
  if (!content) return null;
  // Match ```json ... ``` or ``` ... ``` containing a JSON object
  const fenceRegex = /```(?:json)?\s*\n?([\s\S]*?)\n?```/;
  const fenceMatch = content.match(fenceRegex);
  if (!fenceMatch) return null;

  const jsonString = fenceMatch[1].trim();
  let parsed: RenderPayload;
  try {
    parsed = normalizePayload(JSON.parse(jsonString) as RenderPayload);
  } catch {
    return null;
  }

  // Validate it actually has blocks
  if (!parsed || !parsed.blocks || !Array.isArray(parsed.blocks) || parsed.blocks.length === 0) {
    return null;
  }

  // Extract preamble text before the code fence
  const fenceStart = content.indexOf(fenceMatch[0]);
  const preamble = content.slice(0, fenceStart).trim();

  if (preamble) {
    // Prepend preamble as a text block so the conversational intro is preserved
    parsed.blocks = [
      { type: 'text', version: parsed.version || 'v1', content: preamble } as UIBlock,
      ...parsed.blocks,
    ];
  }

  return parsed;
}

/**
 * Attempts to parse a user message as a UI action event
 */
function parseUIAction(content: string): UIActionEvent | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed && parsed.type === 'ui_action') {
      return parsed as UIActionEvent;
    }
  } catch {
    // Not valid JSON, ignore
  }
  return null;
}

/**
 * Merges user selections into assistant blocks
 */
function mergeSelectionsIntoBlocks(
  payload: RenderPayload,
  userActions: UIActionEvent[]
): RenderPayload {
  if (!payload.blocks || !userActions.length) return payload;

  const updatedBlocks = payload.blocks.map((block) => {
    // Handle select blocks
    if (block.type === 'select') {
      const selectBlock = block as SelectBlock;
      if (selectBlock.content.label) {
        const matchingAction = userActions.find(
          action => action.component === 'select' && action.label && action.label === selectBlock.content.label
        );
        if (matchingAction?.state?.values) {
          const values = matchingAction.state.values;
          if (Array.isArray(values) && values.every(v => typeof v === 'string')) {
            return {
              ...selectBlock,
              content: {
                ...selectBlock.content,
                selectedValues: values
              }
            };
          }
          return block;
        }
      }
    }

    // Handle radio blocks
    if (block.type === 'radio') {
      const radioBlock = block as RadioBlock;
      if (radioBlock.content.label) {
        const matchingAction = userActions.find(
          action => action.component === 'radio' && action.label && action.label === radioBlock.content.label
        );
        if (matchingAction?.state?.value) {
          const value = matchingAction.state.value;
          if (typeof value === 'string') {
            return {
              ...radioBlock,
              content: {
                ...radioBlock.content,
                selectedValue: value
              }
            };
          }
          return block;
        }
      }
    }

    return block;
  });

  return { ...payload, blocks: updatedBlocks as (UIBlock)[] };
}

/**
 * Builds UIBlocks from tool_calls stored in chat history.
 * Used when the backend returns tool call data alongside (or instead of) content.
 * Handles standard blocks, experience tools, grouped blocks, and interactive pre-fills.
 */
function buildBlocksFromToolCalls(
  toolCalls: ChatHistoryToolCall[],
  toolCallOutputs?: ChatHistoryToolCallOutput[]
): (UIBlock | ContainerBlock)[] {
  const blocks: UIBlock[] = [];
  const blockMeta = new Map<number, ToolStreamBlockMeta>();
  const outputMap = new Map<string, string>();

  if (toolCallOutputs) {
    for (const tco of toolCallOutputs) {
      outputMap.set(tco.id, tco.output);
    }
  }

  for (const tc of toolCalls) {
    const blockType = getBlockTypeFromToolName(tc.name);
    if (!blockType) continue;

    let args: Record<string, unknown>;
    try {
      args = JSON.parse(tc.arguments);
    } catch {
      continue;
    }

    // Skip inline_suggestions (they become dynamic suggestions) and
    // routine_draft (it only syncs the preview panel) — neither is a block.
    if (blockType === 'inline_suggestions' || blockType === 'routine_draft') continue;

    // Extract group metadata before building the block
    const { cleanArgs, meta: groupMeta } = extractGroupMeta(args);

    let block: UIBlock;

    if (isExperienceType(blockType)) {
      block = buildExperienceBlock(blockType, cleanArgs, tc.id, true);
    } else if (blockType === 'text') {
      block = { type: 'text', version: '1.0', content: (args.content as string) || '' };
    } else {
      block = { type: blockType, version: '1.0', content: cleanArgs } as UIBlock;
    }

    // Pre-fill interactive blocks with stored outputs so history shows the user's final state
    const output = outputMap.get(tc.id);
    if (output && 'content' in block && block.content && typeof block.content === 'object') {
      try {
        const outputData = JSON.parse(output);
        const content = block.content as Record<string, unknown>;
        if (blockType === 'select' && outputData.values) {
          content.selectedValues = outputData.values;
        }
        if (blockType === 'radio' && outputData.value) {
          content.selectedValue = outputData.value;
        }
        if (blockType === 'editable_list') {
          if (outputData.mode === 'grouped' && outputData.groups) {
            content.groups = outputData.groups;
          } else if (outputData.items) {
            content.items = outputData.items;
          }
        }
        if (blockType === 'confirmation' && outputData.action) {
          content.resolvedAction = outputData.action;
        }
        if (blockType === 'text_input' && typeof outputData.value === 'string') {
          content.submittedValue = outputData.value;
        }
        if (blockType === 'schedule_input' && outputData.cron) {
          content.submittedSchedule = outputData;
        }
        if (blockType === 'channel_input' && outputData.output_channels) {
          content.submittedChannels = outputData;
        }
        if (blockType === 'routine_summary' && outputData.action) {
          content.resolvedAction = outputData.action;
        }
      } catch {
        // ignore parse errors on output
      }
    }

    const blockIndex = blocks.length;
    blocks.push(block);

    // Track group metadata
    if (groupMeta) {
      blockMeta.set(blockIndex, groupMeta);
    }
  }

  // Assemble grouped blocks into containers
  if (blockMeta.size > 0) {
    return assembleGroupedBlocks(blocks, blockMeta);
  }

  return blocks;
}

export function transformChatHistoryToChatEntries(
  chatHistory: ChatHistoryItem[]
): ChatEntry[] {
  const entries: ChatEntry[] = [];

  const assistantActionsMap = new Map<number, UIActionEvent[]>();

  // Identify all user UI actions and map them
  let lastAssistantIndex = -1;
  for (let i = 0; i < chatHistory.length; i++) {
    const item = chatHistory[i];
    if (item.role === 'assistant') {
      lastAssistantIndex = i;
    } else if (item.role === 'user' && lastAssistantIndex >= 0) {
      const action = parseUIAction(item.content);
      if (action) {
        const existing = assistantActionsMap.get(lastAssistantIndex) || [];
        existing.push(action);
        assistantActionsMap.set(lastAssistantIndex, existing);
      }
    }
  }

  // Transform messages and merge selections
  for (let i = 0; i < chatHistory.length; i++) {
    const item = chatHistory[i];

    // User messages - show plain text (skip UI action JSON, show as formatted text)
    if (item.role === 'user') {
      const action = parseUIAction(item.content);
      if (action) {
        // Format UI action as readable text showing what user selected
        let formattedContent = item.content;
        const state = action.state;
        if (state && typeof state === 'object') {
          const labels = Array.isArray(state.labels)
            ? state.labels
            : Array.isArray(state.values)
              ? state.values
              : typeof state.label === 'string'
                ? [state.label]
                : typeof state.value === 'string'
                  ? [state.value]
                  : typeof (state as Record<string, unknown>).summary === 'string'
                    ? [(state as Record<string, unknown>).summary as string]
                    : null;
          if (labels && labels.length > 0) {
            const joined = labels.filter(Boolean).join(', ');
            if (joined) {
              formattedContent = joined;
            }
          }
        }
        entries.push({
          id: item.id,
          kind: 'user',
          content: formattedContent,
          chat_history_id: item.id,
          created_at: item.created_at
        } as ChatEntry);
      } else {
        // Check for a text block with behaviours (e.g. card-triggered report_preview)
        const userBlock = parseUserBlock(item.content);
        if (userBlock) {
          entries.push({
            id: item.id,
            kind: 'user',
            content: userBlock.text,
            blocks: userBlock.blocks,
            ...(userBlock.cardBlock && { cardBlock: userBlock.cardBlock }),
            ...(item.files?.length ? { attachments: item.files } : {}),
            chat_history_id: item.id,
            created_at: item.created_at
          } as ChatEntry);
        } else {
          entries.push({
            id: item.id,
            kind: 'user',
            content: item.content,
            ...(item.files?.length ? { attachments: item.files } : {}),
            chat_history_id: item.id,
            created_at: item.created_at
          } as ChatEntry);
        }
      }
      continue;
    }

    // Skip assistant messages with no content and no tool calls (empty placeholder messages)
    if (item.content == null && (!item.tool_calls || item.tool_calls.length === 0)) {
      continue;
    }

    // Grounding sources persisted with the response, restored onto every
    // assistant entry shape below so the "sources" affordance survives reload.
    const citationsProp = item.citations && item.citations.length > 0
      ? { citations: item.citations }
      : {};

    // Check for tool_calls (tool streaming mode) — reconstruct blocks from tool call arguments
    const uiToolCalls = item.tool_calls?.filter(tc => tc.stream_to_ui);
    if (uiToolCalls && uiToolCalls.length > 0) {
      const toolBlocks = buildBlocksFromToolCalls(uiToolCalls, item.tool_call_outputs);

      // If there's also content, merge content blocks with tool blocks
      let allBlocks: (UIBlock | ContainerBlock)[] = [...toolBlocks];

      if (item.content) {
        try {
          const parsed = normalizePayload(JSON.parse(item.content) as RenderPayload);
          if (parsed.blocks?.length) {
            allBlocks = [...parsed.blocks, ...allBlocks];
          }
        } catch {
          // Content is plain text — prepend as text block
          if (item.content.trim()) {
            allBlocks = [
              { type: 'text', version: '1.0', content: item.content } as UIBlock,
              ...allBlocks
            ];
          }
        }
      }

      if (allBlocks.length > 0) {
        const payload: RenderPayload = { version: 'v1', blocks: allBlocks as UIBlock[] };
        const userActions = assistantActionsMap.get(i) || [];
        const finalPayload = userActions.length > 0
          ? mergeSelectionsIntoBlocks(payload, userActions)
          : payload;

        entries.push({
          id: item.id,
          kind: 'assistant-structured',
          payload: finalPayload,
          chat_history_id: item.id,
          behaviour_action_state: item.behaviour_action_state,
          ...citationsProp
        } as ChatEntry);
      }
      continue;
    }

    // Skip assistant messages with null content that weren't handled by tool call path above
    if (item.content == null) continue;

    // Assistant messages - content is stringified JSON containing blocks (content streaming mode)
    try {
      // Parse the string to get the JSON object with blocks
      let parsed = normalizePayload(JSON.parse(item.content) as RenderPayload);

      // Merge user selections into the blocks
      const userActions = assistantActionsMap.get(i) || [];
      if (userActions.length > 0) {
        parsed = mergeSelectionsIntoBlocks(parsed, userActions);
      }

      // Use assistant-structured for block-based responses
      entries.push({
        id: item.id,
        kind: 'assistant-structured',
        payload: parsed,
        chat_history_id: item.id,
        behaviour_action_state: item.behaviour_action_state,
        ...citationsProp
      } as ChatEntry);
    } catch {
      // JSON.parse failed — content may contain blocks wrapped in markdown code fences
      // (e.g. Claude returns: "Preamble text\n\n```json\n{...blocks...}\n```")
      const extracted = extractBlocksFromCodeFence(item.content);

      if (extracted) {
        // Merge user selections into the extracted blocks
        const userActions = assistantActionsMap.get(i) || [];
        if (userActions.length > 0) {
          const merged = mergeSelectionsIntoBlocks(extracted, userActions);
          entries.push({
            id: item.id,
            kind: 'assistant-structured',
            payload: merged,
            chat_history_id: item.id,
            behaviour_action_state: item.behaviour_action_state,
            ...citationsProp
          } as ChatEntry);
        } else {
          entries.push({
            id: item.id,
            kind: 'assistant-structured',
            payload: extracted,
            chat_history_id: item.id,
            behaviour_action_state: item.behaviour_action_state,
            ...citationsProp
          } as ChatEntry);
        }
      } else {
        // Fallback: no blocks found, treat as plain text
        entries.push({
          id: item.id,
          kind: 'assistant',
          content: item.content,
          chat_history_id: item.id,
          behaviour_action_state: item.behaviour_action_state,
          ...citationsProp
        } as ChatEntry);
      }
    }
  }

  return entries;
}
